import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Layout
import PageLayout from "examples/LayoutContainers/PageLayout";

// Auth
import { useAuth } from "context/AuthContext";
import { useTourContext } from "context/TourContext";
import useTour from "hooks/useTour";
import buocDangNhap from "tours/dangNhap";

// Images
import logo from "assets/images/logo-thu-mua-bo.png";

// Bảng màu/phông chữ thương hiệu "trái bơ bổ đôi" — định nghĩa gốc, dùng
// chung với FormThuMua, XacNhanThanhToan và Sidenav qua assets/theme/avocadoBrand.
import { MAU, FONT_HIEU, FONT_CHU } from "assets/theme/avocadoBrand";
import { oInputAuth, sxNutAuth } from "layouts/authentication/kieuTrangAuth";

// 3 bước có thật của luồng thu mua (xem FormThuMua → XacNhanThanhToan) —
// dùng đúng thứ tự này làm nội dung, không phải số minh hoạ trang trí. Quét
// QR Sọt không còn là 1 bước riêng — gộp thẳng vào từng khối loại bơ ngay
// trong FormThuMua (xem "Quét QR Sọt" trong 1 khối của form).
const CAC_BUOC = [
  { nhan: "Chọn chủ vườn", moTa: "Tra bằng CCCD, số điện thoại hoặc mã QR" },
  { nhan: "Quét sọt & cân từng loại bơ", moTa: "Mỗi loại bơ một khối, tự tính thành tiền" },
  { nhan: "Thanh toán", moTa: "Gộp cả lượt — tự thanh toán hoặc gửi kế toán" },
];

// Line-art trái bơ bổ đôi, nét đơn — dáng lê lệch (thon ở cuống, đầy ở đáy)
// để không lẫn với vòng tròn trang trí chung chung; hạt tô đặc làm điểm nhấn.
function HinhTraiBo({ width, opacity, sx }) {
  const w = Number(width);
  const h = Math.round((w * 260) / 200);
  return (
    <MDBox sx={{ position: "absolute", pointerEvents: "none", lineHeight: 0, ...sx }}>
      <svg viewBox="0 0 200 260" width={w} height={h} fill="none" aria-hidden style={{ opacity }}>
        <path
          d="M100 4C136 4 160 32 168 78C176 126 166 182 138 220C122 242 106 254 92 254C76 254 58 240 42 214C18 176 10 122 20 76C29 34 62 4 100 4Z"
          stroke={MAU.pit}
          strokeWidth="1.75"
        />
        <circle
          cx="103"
          cy="158"
          r="38"
          fill={MAU.pit}
          fillOpacity="0.28"
          stroke={MAU.pit}
          strokeWidth="1.75"
        />
      </svg>
    </MDBox>
  );
}

HinhTraiBo.propTypes = {
  width: PropTypes.number.isRequired,
  opacity: PropTypes.number.isRequired,
  sx: PropTypes.object.isRequired,
};

function NhanTruong({ children }) {
  return (
    <MDTypography
      variant="caption"
      sx={{
        display: "block",
        mb: 0.75,
        fontFamily: FONT_CHU,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: alpha(MAU.ink, 0.65),
      }}
    >
      {children}
    </MDTypography>
  );
}

NhanTruong.propTypes = {
  children: PropTypes.node.isRequired,
};

function DangNhap() {
  const { user, login, loiXacThuc } = useAuth();
  const { active: coTour, startActiveTour } = useTourContext();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [matKhau, setMatKhau] = useState("");
  const [hienMatKhau, setHienMatKhau] = useState(false);
  const [ghiNho, setGhiNho] = useState(true);
  const [loi, setLoi] = useState("");
  const [dangDangNhap, setDangDangNhap] = useState(false);

  useTour("dang-nhap", buocDangNhap());

  if (user) return <Navigate to={location.state?.from ?? "/"} replace />;

  async function handleSubmit(e) {
    e.preventDefault();
    setLoi("");
    setDangDangNhap(true);
    try {
      await login(email, matKhau, ghiNho);
    } catch {
      setLoi("Sai email hoặc mật khẩu.");
      setDangDangNhap(false);
    }
  }

  return (
    <PageLayout>
      <MDBox
        width="100%"
        minHeight="100vh"
        sx={{ backgroundColor: MAU.paper, overflowX: "hidden" }}
      >
        <Grid container sx={{ minHeight: "100vh" }}>
          <Grid
            item
            xs={12}
            sx={{
              display: { xs: "flex", lg: "none" },
              flexDirection: "column",
              justifyContent: "flex-end",
              background: `linear-gradient(160deg, ${MAU.skin} 0%, ${MAU.skinDeep} 100%)`,
              px: 3,
              pt: 5,
              pb: 3,
            }}
          >
            <MDTypography
              sx={{
                fontFamily: FONT_CHU,
                fontWeight: 700,
                fontSize: "0.72rem",
                letterSpacing: "0.22em",
                color: MAU.flesh,
              }}
            >
              HỆ THỐNG THU MUA
            </MDTypography>
            <MDTypography
              sx={{
                fontFamily: FONT_HIEU,
                fontWeight: 600,
                fontSize: "1.75rem",
                lineHeight: 1.2,
                color: MAU.paper,
                mt: 1,
              }}
            >
              Cân tại vườn. Trả tận tay.
            </MDTypography>
          </Grid>
          {/* Panel thương hiệu */}
          <Grid
            item
            xs={12}
            lg={7}
            sx={{
              position: "relative",
              overflow: "hidden",
              background: `linear-gradient(160deg, ${MAU.skin} 0%, ${MAU.skinDeep} 100%)`,
              display: { xs: "none", lg: "flex" },
              flexDirection: "column",
              justifyContent: "center",
              px: { lg: 8 },
              minHeight: "auto",
            }}
          >
            <HinhTraiBo width={380} opacity={0.85} sx={{ right: "-10px", bottom: "-40px" }} />
            <HinhTraiBo width={160} opacity={0.18} sx={{ left: "10px", top: "-30px" }} />

            <MDBox sx={{ position: "relative", maxWidth: "460px" }}>
              <MDTypography
                sx={{
                  fontFamily: FONT_CHU,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: "0.22em",
                  color: MAU.flesh,
                }}
              >
                HỆ THỐNG THU MUA
              </MDTypography>
              <MDTypography
                sx={{
                  fontFamily: FONT_HIEU,
                  fontWeight: 600,
                  fontSize: "3rem",
                  lineHeight: 1.12,
                  color: MAU.paper,
                  mt: 1.5,
                }}
              >
                Cân tại vườn.
                <br />
                Trả tận tay.
              </MDTypography>
              <MDTypography
                sx={{
                  fontFamily: FONT_CHU,
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                  color: alpha(MAU.paper, 0.72),
                  mt: 2,
                }}
              >
                Theo dõi từng lượt cân, từng Sọt, từng lần thanh toán — từ vườn đến kho.
              </MDTypography>

              <MDBox sx={{ mt: 5 }}>
                {CAC_BUOC.map((b, i) => (
                  <MDBox key={b.nhan} display="flex" gap={2} mb={i < CAC_BUOC.length - 1 ? 2.5 : 0}>
                    <MDBox
                      sx={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        border: `1.5px solid ${alpha(MAU.pit, 0.7)}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: FONT_HIEU,
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        color: MAU.pit,
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </MDBox>
                    <MDBox>
                      <MDTypography
                        sx={{
                          fontFamily: FONT_CHU,
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          color: MAU.paper,
                        }}
                      >
                        {b.nhan}
                      </MDTypography>
                      <MDTypography
                        sx={{
                          fontFamily: FONT_CHU,
                          fontSize: "0.8rem",
                          color: alpha(MAU.paper, 0.6),
                        }}
                      >
                        {b.moTa}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                ))}
              </MDBox>
            </MDBox>
          </Grid>

          {/* Panel đăng nhập */}
          <Grid
            item
            xs={12}
            lg={5}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 3, sm: 6, lg: 7 },
              py: { xs: 5, lg: 0 },
            }}
          >
            <MDBox sx={{ width: "100%", maxWidth: "360px", mx: "auto", position: "relative" }}>
              {coTour && (
                <IconButton
                  size="small"
                  aria-label="Xem lại hướng dẫn trang này"
                  onClick={startActiveTour}
                  sx={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    color: alpha(MAU.ink, 0.55),
                  }}
                >
                  <Icon>help_outline</Icon>
                </IconButton>
              )}
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent={{ xs: "center", lg: "flex-start" }}
                gap={1.5}
                mb={{ xs: 4, lg: 6 }}
              >
                <MDBox
                  component="img"
                  src={logo}
                  alt="Thu mua bơ"
                  sx={{ width: "64px", height: "64px", borderRadius: "50%" }}
                />
                <MDTypography
                  sx={{
                    fontFamily: FONT_HIEU,
                    fontWeight: 600,
                    fontSize: "1.3rem",
                    color: MAU.ink,
                  }}
                >
                  Thu mua bơ
                </MDTypography>
              </MDBox>

              <MDTypography
                sx={{
                  fontFamily: FONT_HIEU,
                  fontWeight: 600,
                  fontSize: "1.9rem",
                  color: MAU.ink,
                  mb: 0.5,
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                Đăng nhập
              </MDTypography>
              <MDTypography
                sx={{
                  fontFamily: FONT_CHU,
                  fontSize: "0.9rem",
                  color: alpha(MAU.ink, 0.6),
                  mb: 4,
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                Dùng tài khoản được cấp để vào hệ thống.
              </MDTypography>

              <MDBox component="form" role="form" onSubmit={handleSubmit}>
                <MDBox mb={2.5}>
                  <NhanTruong>Email</NhanTruong>
                  <MDInput
                    type="email"
                    placeholder="ten@congty.vn"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                    sx={oInputAuth}
                  />
                </MDBox>
                <MDBox mb={1}>
                  <NhanTruong>Mật khẩu</NhanTruong>
                  <MDInput
                    type={hienMatKhau ? "text" : "password"}
                    placeholder="••••••••"
                    value={matKhau}
                    onChange={(e) => setMatKhau(e.target.value)}
                    fullWidth
                    required
                    sx={oInputAuth}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setHienMatKhau((v) => !v)}
                            edge="end"
                            size="small"
                            tabIndex={-1}
                            aria-label={hienMatKhau ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                            sx={{ color: alpha(MAU.ink, 0.5) }}
                          >
                            <Icon fontSize="small">
                              {hienMatKhau ? "visibility_off" : "visibility"}
                            </Icon>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>

                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                >
                  <MDBox
                    id="tour-dang-nhap-ghi-nho"
                    display="flex"
                    alignItems="center"
                    component="label"
                    sx={{ cursor: "pointer", width: "fit-content" }}
                  >
                    <Checkbox
                      checked={ghiNho}
                      onChange={(e) => setGhiNho(e.target.checked)}
                      disableRipple
                      sx={{
                        p: 0.5,
                        ml: -0.5,
                        "& .MuiSvgIcon-root": { borderColor: alpha(MAU.skin, 0.4) },
                        "&.Mui-checked .MuiSvgIcon-root": {
                          borderColor: MAU.skin,
                          backgroundColor: MAU.skin,
                          backgroundImage:
                            "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 -1 22 22'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2.5' d='M6 10l3 3l6-6'/%3e%3c/svg%3e\")",
                        },
                      }}
                    />
                    <MDTypography
                      sx={{
                        fontFamily: FONT_CHU,
                        fontSize: "0.85rem",
                        color: alpha(MAU.ink, 0.75),
                      }}
                    >
                      Ghi nhớ đăng nhập
                    </MDTypography>
                  </MDBox>

                  <MDTypography
                    id="tour-dang-nhap-quen-mat-khau"
                    component={Link}
                    to="/dat-lai-mat-khau"
                    sx={{
                      fontFamily: FONT_CHU,
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: MAU.skinDeep,
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Quên mật khẩu?
                  </MDTypography>
                </MDBox>

                {(loi || loiXacThuc) && (
                  <MDBox mt={2}>
                    <MDAlert color="error">{loi || loiXacThuc}</MDAlert>
                  </MDBox>
                )}

                <MDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={dangDangNhap}
                  sx={{ mt: 4, ...sxNutAuth }}
                >
                  {dangDangNhap ? "Đang đăng nhập…" : "Đăng nhập"}
                </MDButton>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </PageLayout>
  );
}

export default DangNhap;
